import { AnalyticsProvider, useAnalyticsContext } from '@bbld/bbl-analytics'
import history from 'browserHistory'
import { FC, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { IBBL } from 'shared/models/BBL'
import { UserRoleEnum } from 'shared/models/User'
import { getActiveClient } from 'shared/store/clients/selectors'
import { getUserRoles } from 'shared/store/user/selectors'
import { getRolesForClient, hasBoardMemberRole } from 'shared/utils/rolesHelper'

interface IProps {
  bbl: IBBL
  children?: React.ReactNode
}

const getBoardRoleLabel = (
  roles: ReturnType<typeof getRolesForClient>
): string | undefined => {
  if (roles.some((r) => r.role === UserRoleEnum.boardleader))
    return 'Styreleder'
  if (roles.some((r) => hasBoardMemberRole(r.role as UserRoleEnum)))
    return 'Styremedlem'
  return undefined
}

const DataLayerSync: FC<{ children?: React.ReactNode }> = ({ children }) => {
  const analytics = useAnalyticsContext()
  const activeClient = useSelector(getActiveClient)
  const userRoles = useSelector(getUserRoles)

  useEffect(() => {
    const rolesForClient = getRolesForClient(
      userRoles.data ?? [],
      activeClient?.id
    )
    const boardRoleLabel = getBoardRoleLabel(rolesForClient)

    analytics.setDataLayerVariables({
      customer: activeClient?.clientName ?? '',
      ...(boardRoleLabel !== undefined
        ? { role: boardRoleLabel }
        : { role: undefined }),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClient?.clientName, activeClient?.id, userRoles.data])

  return <>{children}</>
}

const AnalyticsHandler: FC<IProps> = ({ bbl, children }) => {
  const analyticsSettings = {
    gtmCodes: [window.config.bblPivotalGtmCode],
    siteName: window.config.appName,
    bblCode: window.config.bblCode,
    ngtApp: Boolean(window.ReactNativeWebView),
  }
  if (bbl.settings.gtmId) {
    analyticsSettings.gtmCodes.push('GTM-' + bbl.settings.gtmId)
  }

  return (
    <AnalyticsProvider settings={analyticsSettings} history={history}>
      <DataLayerSync>{children}</DataLayerSync>
    </AnalyticsProvider>
  )
}

export default AnalyticsHandler
